import React, { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import RadioFieldset from 'tailwind-components/RadioFieldset/RadioFieldset';
import TextField from 'tailwind-components/TextField/TextField';
import { isAgentRankLimited } from 'helpers/agents';
import { AGENCY_DISABLED_CARRIER_MESSAGE } from 'helpers/carriers/carriers';
import {
  buildInitialNeoForm,
  buildNeoQuestionsPayload,
  getNeoFieldErrors,
  isNeoAdmin,
  isNeoFormValid,
  NEO_QUESTIONS
} from 'helpers/neo/neo';
import {
  createNeoContractingRequest,
  setRequestedCarrierName
} from 'state/actions/appointments';
import { AppointmentMessages } from './AppointmentMessages';
import SendRequestButton from './SendRequestButton';

const YES_NO_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' }
];

/**
 * contracting form shown in the sidebar for Neo Insurance, which has no states or transfer type
 * @param carrierData - carrier object built by the carrier factory
 * @param obFormCompleted - whether the agent completed the initial onboarding form
 * @param isAgencyDisabled - whether the agency disabled this carrier for its agents
 * @param isRankRestricted - whether the carrier is closed to the agent's rank
 * @param isCarrierDisabledDueToBlackout - whether a blackout period currently blocks requests
 * @param carrierDisabledDueToBlackoutMessage - message explaining the active blackout
 * @param sectionRef - ref on the section wrapper, used to scroll the form into view
 * @param sendButtonRef - ref on the send request button, used to track its visibility
 */
export default function NeoContractingSection({
  carrierData,
  obFormCompleted,
  isAgencyDisabled,
  isRankRestricted,
  isCarrierDisabledDueToBlackout,
  carrierDisabledDueToBlackoutMessage,
  sectionRef,
  sendButtonRef
}) {
  const dispatch = useDispatch();
  const { isSendingRequestAppointments } = useSelector(
    state => state.appointments
  );
  const user = useSelector(state => state.user.zohoData);

  const isAdmin = isNeoAdmin(user);
  const isAgentLimited = isAgentRankLimited(user.Rank);
  const areApptsDisabled = Boolean(carrierData.appointmentsDisabled);

  const [form, setForm] = useState(() => buildInitialNeoForm(user));
  // the email format is only flagged once the agent leaves the field, so it does not fight typing
  const [isEmailTouched, setIsEmailTouched] = useState(false);

  useEffect(() => {
    setForm(buildInitialNeoForm(user));
    setIsEmailTouched(false);
  }, [carrierData.id]);

  const setField = (key, value) => {
    setForm(prevState => ({ ...prevState, [key]: value }));
  };

  const fieldErrors = getNeoFieldErrors(form, isAdmin);

  const isDisabled =
    !obFormCompleted ||
    isAgencyDisabled ||
    isSendingRequestAppointments ||
    isCarrierDisabledDueToBlackout ||
    isAgentLimited ||
    isRankRestricted ||
    areApptsDisabled ||
    !isNeoFormValid(form, isAdmin);

  // the form replaces the states/transfer UI, so it hides for the same blockers that hide those
  const showForm = !isAgencyDisabled && !isRankRestricted && !areApptsDisabled;

  const handleSubmit = async () => {
    if (isDisabled) return;

    // makes the carrier card show the sending state while the request is in flight
    dispatch(setRequestedCarrierName(carrierData.name));
    const isSuccess = await dispatch(
      createNeoContractingRequest({
        carrierId: carrierData.id,
        questions: buildNeoQuestionsPayload(form, isAdmin)
      })
    );

    if (isSuccess) setForm(buildInitialNeoForm(user)); // reset the form state
  };

  return (
    <div
      ref={sectionRef}
      className='mt-2.5 pt-2.5 border-t border-grey-lightest first:mt-0 first:border-t-0 first:pt-0'
    >
      <p className='text-black text-base font-medium tracking-wide mb-3 mt-3 text-left m-0'>
        Get Contracted
      </p>

      {isAgencyDisabled && (
        <p className='text-left leading-tight text-semantic-danger m-0 mb-3'>
          {AGENCY_DISABLED_CARRIER_MESSAGE}
        </p>
      )}

      {showForm && (
        <div className='flex flex-col gap-4 mb-4 text-left'>
          <TextField
            label={NEO_QUESTIONS.name.label}
            name='neoName'
            placeholder={NEO_QUESTIONS.name.placeholder}
            value={form.name}
            error={fieldErrors.name}
            onChange={value => setField('name', value)}
            autoComplete='name'
          />
          <TextField
            label={NEO_QUESTIONS.email.label}
            name='neoEmail'
            type='email'
            placeholder={NEO_QUESTIONS.email.placeholder}
            value={form.email}
            error={isEmailTouched ? fieldErrors.email : ''}
            onChange={value => setField('email', value)}
            onBlur={() => setIsEmailTouched(true)}
            autoComplete='email'
          />
          <TextField
            label={NEO_QUESTIONS.phone.label}
            name='neoPhone'
            type='tel'
            inputMode='tel'
            placeholder={NEO_QUESTIONS.phone.placeholder}
            value={form.phone}
            error={fieldErrors.phone}
            onChange={value => setField('phone', value)}
            autoComplete='tel'
          />
          <TextField
            label={NEO_QUESTIONS.npn.label}
            name='neoNpn'
            inputMode='numeric'
            placeholder={NEO_QUESTIONS.npn.placeholder}
            value={form.npn}
            error={fieldErrors.npn}
            onChange={value => setField('npn', value)}
          />

          <RadioFieldset
            legend={NEO_QUESTIONS.independentAgent.label}
            name='neoIndependentAgent'
            value={form.independentAgent}
            onChange={value => setField('independentAgent', value)}
            options={YES_NO_OPTIONS}
          />

          {isAdmin && (
            <RadioFieldset
              legend={NEO_QUESTIONS.advancingCommissions.label}
              name='neoAdvancingCommissions'
              value={form.advancingCommissions}
              onChange={value => setField('advancingCommissions', value)}
              options={YES_NO_OPTIONS}
            />
          )}

          {isAdmin && (
            <TextField
              label={NEO_QUESTIONS.agentsToContract.label}
              name='neoAgentsToContract'
              inputMode='numeric'
              placeholder={NEO_QUESTIONS.agentsToContract.placeholder}
              value={form.agentsToContract}
              error={fieldErrors.agentsToContract}
              onChange={value => setField('agentsToContract', value)}
            />
          )}

          <RadioFieldset
            legend={NEO_QUESTIONS.interestedInLeads.label}
            name='neoInterestedInLeads'
            value={form.interestedInLeads}
            onChange={value => setField('interestedInLeads', value)}
            options={YES_NO_OPTIONS}
          />
        </div>
      )}

      {!isAgencyDisabled && (
        <AppointmentMessages
          isInitialOnboardingFormIncomplete={!obFormCompleted}
          isFfmCertificationBlocked={false}
          areApptsDisabled={areApptsDisabled}
          hasOpenTransferRequests={false}
          statesAvailableButNoLicenses={false}
          isBlockedByBcbsCrossRequest={false}
          isCarrierDisabledDueToBlackout={isCarrierDisabledDueToBlackout}
          carrierDisabledDueToBlackoutMessage={
            carrierDisabledDueToBlackoutMessage
          }
        />
      )}

      {!isAgencyDisabled && !isRankRestricted && (
        <SendRequestButton
          ref={sendButtonRef}
          isDisabled={Boolean(isDisabled)}
          isSendingRequestAppointments={isSendingRequestAppointments}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}

NeoContractingSection.propTypes = {
  carrierData: propTypes.object.isRequired,
  obFormCompleted: propTypes.bool,
  isAgencyDisabled: propTypes.bool,
  isRankRestricted: propTypes.bool,
  isCarrierDisabledDueToBlackout: propTypes.bool,
  carrierDisabledDueToBlackoutMessage: propTypes.string,
  sectionRef: propTypes.object,
  sendButtonRef: propTypes.object
};
